// src/pages/orders/BulkOrderCreatePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  Loader2,
  RefreshCw,
  Send,
  Columns,
} from 'lucide-react';
import { getMyUsage } from '../../services/api/subscription';
import { getBulkOrderSettings, saveBulkOrderSettings } from '../../services/api/bulkOrderSettings';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { useAuth } from '../../hooks/useAuth';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import QuotaWarning from '../../components/common/Subscription/QuotaWarning';
import useBulkOrder from '../../hooks/useBulkOrder';
import ShopSelector from './components/bulk/ShopSelector';
import BulkShiftSelector from './components/bulk/BulkShiftSelector';
import BulkAssignUsers from './components/bulk/BulkAssignUsers';
import LeadUploadOptions from './components/bulk/LeadUploadOptions';
import FileUploadZone from '../../components/common/bulk/FileUploadZone';
import DataPreviewTable from './components/bulk/DataPreviewTable';
import ValidationResults from '../../components/common/bulk/ValidationResults';
import ProgressIndicator from '../../components/common/bulk/ProgressIndicator';
import ResultsSummary from '../../components/common/bulk/ResultsSummary';
import DownloadTemplateModal from './components/bulk/DownloadTemplateModal';
import ColumnMappingModal from '../../components/common/bulk/ColumnMappingModal';
import styles from './BulkOrderCreatePage.module.css';

// The canonical column names used in our standard bulk-order template
const MAPPABLE_FIELDS = [
  { key: 'Customer_Name',      label: 'Customer Name',      required: true  },
  { key: 'Address',            label: 'Address',            required: true  },
  { key: 'City_Name',          label: 'City Name',          required: true  },
  { key: 'Phone_1',            label: 'Phone 1',            required: true  },
  { key: 'Phone_2',            label: 'Phone 2',            required: false },
  { key: 'Customer_Email',     label: 'Customer Email',     required: false },
  { key: 'Private_Reference',  label: 'Private Reference',  required: false },
  { key: 'Delivery_Notes',     label: 'Delivery Notes',     required: false },
];

const BulkOrderCreatePage = () => {
  const navigate   = useNavigate();
  const { t }      = useLocalization();
  const { success, error: showError } = useAlert();
  const { user } = useAuth();
  const { settings: messageCenterSettings } = useBusinessSettings('messageCenter');

  const [showTemplateModal,    setShowTemplateModal]    = useState(false);
  const [showMappingModal,     setShowMappingModal]     = useState(false);
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const [quotaInfo,            setQuotaInfo]            = useState(null);
  const [isLoadingQuota,       setIsLoadingQuota]       = useState(true);

  const {
    selectedShop,
    selectedShiftId,
    uploadedFile,
    parsedData,
    validationResults,
    submissionResults,
    isProcessing,
    processingStep,
    progress,
    selectedAssignUserIds,
    defaultProduct,
    useCityUnknown,
    duplicateCheckDays,
    columnMappings,
    duplicateRows,
    blacklistedRowNumbers,
    blacklistedCount,

    setSelectedShop,
    setSelectedShiftId,
    setSelectedAssignUserIds,
    setDefaultProduct,
    setUseCityUnknown,
    setDuplicateCheckDays,
    setColumnMappings,
    handleFileUpload,
    handleValidation,
    handleSubmit,
    handleRetryValidation,
    removeInvalidRow,
    removeAllDuplicates,
    removeBlacklistedRows,
    updateInvalidRow,
    clearAll,
    downloadTemplate,
  } = useBulkOrder();

  // ── Load quota ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchQuota = async () => {
      try {
        setIsLoadingQuota(true);
        const response = await getMyUsage();
        setQuotaInfo(response.usage?.ecomOrders || null);
      } catch (err) {
        console.error('Failed to fetch quota:', err);
      } finally {
        setIsLoadingQuota(false);
      }
    };
    fetchQuota();
  }, []);

  // ── Load saved column mappings on mount ───────────────────────────────────
  useEffect(() => {
    const loadMappings = async () => {
      try {
        const settings = await getBulkOrderSettings();
        if (settings.columnMappings) setColumnMappings(settings.columnMappings);
      } catch (err) {
        console.error('Failed to load column mappings:', err);
      }
    };
    loadMappings();
  }, []);

  // ── Step helper ────────────────────────────────────────────────────────────
  const getStepStatus = (step) => {
    const steps        = ['select', 'upload', 'validate', 'submit'];
    const currentIndex = steps.indexOf(processingStep);
    const stepIndex    = steps.indexOf(step);
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'active';
    return 'pending';
  };

  const isConfigLocked = parsedData.length > 0;

  const isResellerDropshipper =
    selectedShop?.type === 'dropshipper' &&
    selectedShop?.resellerBusinessId != null &&
    parseInt(selectedShop.resellerBusinessId) === parseInt(user?.businessId);

  // ── Template ───────────────────────────────────────────────────────────────
  const handleDownloadTemplateClick = () => {
    if (!selectedShop) { showError('Please select a shop first'); return; }
    setShowTemplateModal(true);
  };

  const handleDownloadTemplate = async (options) => {
    setIsGeneratingTemplate(true);
    try {
      await downloadTemplate(options);
      success('Template downloaded successfully!');
      setShowTemplateModal(false);
    } catch (err) {
      showError(err.message || 'Failed to generate template');
    } finally {
      setIsGeneratingTemplate(false);
    }
  };

  // ── Error-wrapped handlers ─────────────────────────────────────────────────
  const handleFileUploadWithError = async (file) => {
    try { await handleFileUpload(file); }
    catch (err) { showError(err.message || 'Failed to parse the uploaded file.'); }
  };

  const handleValidationWithError = async () => {
    try { await handleValidation(); }
    catch (err) { showError(err.message || 'Validation failed. Please try again.'); }
  };

  const handleRetryValidationWithError = async () => {
    try { await handleRetryValidation(); }
    catch (err) { showError(err.message || 'Retry validation failed.'); }
  };

  const handleSubmitWithError = async () => {
    try { await handleSubmit(); }
    catch (err) { showError(err.message || 'Order submission failed.'); }
  };

  // ── Quota helpers ─────────────────────────────────────────────────────────
  const getQuotaSeverity = () => {
    if (!quotaInfo || quotaInfo.isUnlimited) return 'info';
    const pct = quotaInfo.limit > 0 ? (quotaInfo.usage / quotaInfo.limit) * 100 : 0;
    if (pct >= 90) return 'critical';
    if (pct >= 80) return 'warning';
    return 'info';
  };

  const isQuotaExceeded = () => (!quotaInfo || quotaInfo.isUnlimited) ? false : quotaInfo.usage >= quotaInfo.limit;

  const wouldExceedQuota = () => {
    if (!quotaInfo || quotaInfo.isUnlimited) return false;
    const validCount = validationResults?.summary?.validCount || 0;
    return (quotaInfo.usage + validCount) > quotaInfo.limit;
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const duplicateCount      = Object.keys(duplicateRows).length;
  const activeMappingCount  = Object.values(columnMappings).filter(v => Array.isArray(v) ? v.length > 0 : v?.trim()).length;

  const canValidate = selectedShop && parsedData.length > 0 && !isProcessing;
  const canSubmit   =
    validationResults &&
    validationResults.summary?.validCount > 0 &&
    validationResults.results?.every(r => r.status === 'valid') &&
    !isProcessing &&
    !wouldExceedQuota();

  return (
    <div className={styles.bulkOrderPage}>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <FileSpreadsheet className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('ecomOrders.bulkOrderCreate')}</h1>
              <p>{t('ecomOrders.bulkOrderCreateDesc')}</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button
              onClick={() => setShowMappingModal(true)}
              className={`${styles.mappingButton} ${activeMappingCount > 0 ? styles.mappingButtonActive : ''}`}
              title="Configure column mappings for custom Excel headers"
            >
              <Columns size={16} />
              Column Mapping
              {activeMappingCount > 0 && (
                <span className={styles.mappingBadge}>{activeMappingCount}</span>
              )}
            </button>
            <button
              onClick={handleDownloadTemplateClick}
              className={styles.downloadButton}
              title={t('ecomOrders.downloadTemplate')}
              disabled={!selectedShop}
            >
              <Download size={18} />
              {t('ecomOrders.downloadTemplate')}
            </button>
            <button onClick={() => navigate('/orders')} className={styles.backButton}>
              <ArrowLeft size={18} />
              {t('ecomOrders.backToOrders')}
            </button>
          </div>
        </div>
      </div>

      {/* ── Progress Steps ───────────────────────────────────────────────────── */}
      <div className={styles.progressSteps}>
        {['select', 'upload', 'validate', 'submit'].map((step, i, arr) => (
          <React.Fragment key={step}>
            <div className={`${styles.step} ${styles[getStepStatus(step)]}`}>
              <div className={styles.stepNumber}>{i + 1}</div>
              <div className={styles.stepLabel}>
                {step === 'select'   && t('ecomOrders.selectShop')}
                {step === 'upload'   && t('ecomOrders.uploadFile')}
                {step === 'validate' && t('ecomOrders.validateOrders')}
                {step === 'submit'   && t('ecomOrders.submitOrders')}
              </div>
            </div>
            {i < arr.length - 1 && <div className={styles.stepConnector} />}
          </React.Fragment>
        ))}
      </div>

      <div className={styles.pageContent}>
        {/* Quota warning */}
        {!isLoadingQuota && quotaInfo && !submissionResults && (
          <QuotaWarning
            resourceType="ecomOrders"
            usage={quotaInfo}
            severity={getQuotaSeverity()}
            dismissible={!isQuotaExceeded()}
          />
        )}

        {/* Quota exceeded for bulk */}
        {wouldExceedQuota() && validationResults && (
          <div className={styles.quotaExceededBanner}>
            <AlertCircle size={20} />
            <div>
              <p>
                {t('subscription.bulkOrderExceedsQuota', {
                  validCount: validationResults.summary.validCount,
                  remaining:  Math.max(0, quotaInfo.limit - quotaInfo.usage),
                })}
              </p>
            </div>
          </div>
        )}

        {/* ── Row 1: Shop + Assign Users (+ Message Center Shift, when eligible) ── */}
        {!submissionResults && (() => {
          const showShiftColumn = messageCenterSettings.enabled && selectedShop?.messageCenterShiftEligible;
          return (
            <div className={`${styles.configRow} ${showShiftColumn ? styles.configRowWithShift : ''}`}>
              <div className={styles.configRowShop}>
                <ShopSelector
                  selectedShop={selectedShop}
                  onShopChange={setSelectedShop}
                  disabled={isConfigLocked}
                />
              </div>
              <div className={styles.configRowAssign}>
                <BulkAssignUsers
                  selectedUserIds={selectedAssignUserIds}
                  onUserIdsChange={setSelectedAssignUserIds}
                  disabled={isConfigLocked}
                />
              </div>
              {showShiftColumn && (
                <div className={styles.configRowShift}>
                  <BulkShiftSelector
                    selectedShiftId={selectedShiftId}
                    onShiftChange={setSelectedShiftId}
                    disabled={isConfigLocked}
                  />
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Row 2: Lead Upload Options (full width) ─────────────────────── */}
        {!submissionResults && (
          <LeadUploadOptions
            shopId={selectedShop?.id}
            defaultProduct={defaultProduct}
            onDefaultProductChange={setDefaultProduct}
            useCityUnknown={useCityUnknown}
            onUseCityUnknownChange={setUseCityUnknown}
            duplicateCheckDays={duplicateCheckDays}
            onDuplicateCheckDaysChange={setDuplicateCheckDays}
            disabled={isConfigLocked}
            isResellerDropshipper={isResellerDropshipper}
          />
        )}

        {/* File Upload */}
        {selectedShop && !submissionResults && (
          <FileUploadZone
            uploadedFile={uploadedFile}
            onFileUpload={handleFileUploadWithError}
            disabled={isProcessing}
          />
        )}

        {/* Data Preview */}
        {parsedData.length > 0 && !submissionResults && (
          <DataPreviewTable
            data={parsedData}
            validationResults={validationResults}
            onRemoveRow={removeInvalidRow}
            onUpdateRow={updateInvalidRow}
            shopId={selectedShop?.id}
            duplicateRows={duplicateRows}
            onRemoveAllDuplicates={duplicateCount > 0 ? removeAllDuplicates : undefined}
            blacklistedRowNumbers={blacklistedRowNumbers}
            onRemoveAllBlacklisted={blacklistedCount > 0 ? removeBlacklistedRows : undefined}
            isResellerDropshipper={isResellerDropshipper}
          />
        )}

        {/* Validation Results */}
        {validationResults && !submissionResults && (
          <ValidationResults
            results={validationResults}
            onRetry={handleRetryValidationWithError}
            isProcessing={isProcessing}
          />
        )}

        {/* Processing Indicator */}
        {isProcessing && (
          <ProgressIndicator step={processingStep} progress={progress} />
        )}

        {/* Results Summary */}
        {submissionResults && (
          <ResultsSummary
            results={submissionResults}
            onCreateMore={clearAll}
            onViewResults={() => navigate('/orders')}
          />
        )}

        {/* ── Action Buttons ─────────────────────────────────────────────── */}
        {parsedData.length > 0 && !submissionResults && (
          <div className={styles.actionButtons}>
            <button onClick={clearAll} className={styles.clearButton} disabled={isProcessing}>
              <XCircle size={18} />
              {t('ecomOrders.clearAll')}
            </button>

            {!validationResults && (
              <button onClick={handleValidationWithError} className={styles.validateButton} disabled={!canValidate}>
                {isProcessing
                  ? <Loader2 size={18} className={styles.spinning} />
                  : <CheckCircle size={18} />}
                {t('ecomOrders.validateOrders')}
              </button>
            )}

            {validationResults?.results?.some(r => r.status === 'failed') && (
              <button onClick={handleRetryValidationWithError} className={styles.retryButton} disabled={isProcessing}>
                <RefreshCw size={18} />
                {t('ecomOrders.retryValidation')}
              </button>
            )}

            {canSubmit && (
              <button onClick={handleSubmitWithError} className={styles.submitButton} disabled={!canSubmit}>
                {isProcessing
                  ? <Loader2 size={18} className={styles.spinning} />
                  : <Send size={18} />}
                {t('ecomOrders.submitValidOrders', {
                  count: validationResults.summary.validCount - duplicateCount,
                })}
                {duplicateCount > 0 && (
                  <span className={styles.dupExcludedNote}>
                    ({duplicateCount} duplicates excluded)
                  </span>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      {showTemplateModal && (
        <DownloadTemplateModal
          onClose={() => setShowTemplateModal(false)}
          onDownload={handleDownloadTemplate}
          isGenerating={isGeneratingTemplate}
        />
      )}

      {showMappingModal && (
        <ColumnMappingModal
          onClose={() => setShowMappingModal(false)}
          onSaved={(newMappings) => {
            setColumnMappings(newMappings);
            success('Column mappings saved!');
          }}
          fields={MAPPABLE_FIELDS}
          getSettings={getBulkOrderSettings}
          saveSettings={saveBulkOrderSettings}
          title="Column Mappings"
          infoText="Map your Excel column headers to the standard order fields. Add multiple aliases per field — the first matching column in your file will be used. Leave empty to use the default column name."
        />
      )}
    </div>
  );
};

export default BulkOrderCreatePage;